import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router';
import { Icon, type IconName } from './Icon';
import styles from './Button.module.css';

import { useT } from '@/i18n/languageStore';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'success' | 'danger';
export type ButtonSize = 'md' | 'sm';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon. */
  icon?: IconName;
  /** Stretch to the container width (the design's default `.btn` is full width on phones). */
  block?: boolean;
  className?: string;
  children: ReactNode;
}

export interface ButtonProps
  extends CommonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Busy: announced to screen readers, and further presses are ignored. */
  loading?: boolean;
  to?: undefined;
}

export interface LinkButtonProps extends CommonProps {
  /** Renders a real link (navigation) styled as a button. */
  to: string;
  replace?: boolean;
  /** When the visible text alone is not enough ("Chat" → "Chat with Sam Rivera"). */
  'aria-label'?: string;
}

/**
 * The design's `.btn` (min-height 48, 14px radius, Nunito 700) with the same
 * variants as mobile/src/components/ui/Button.tsx. Navigation uses `to`, so it
 * stays a link (middle-click, open in new tab) rather than a button.
 */
export function Button(props: ButtonProps | LinkButtonProps) {
  const t = useT();
  const { variant = 'primary', size = 'md', icon, block = false, className, children } = props;
  const classes = [styles.button, block && styles.block, className].filter(Boolean).join(' ');
  const content = (
    <>
      {icon ? <Icon name={icon} size={size === 'sm' ? 16 : 18} /> : null}
      <span>{children}</span>
    </>
  );

  if (props.to !== undefined) {
    return (
      <Link
        to={props.to}
        replace={props.replace}
        aria-label={props['aria-label'] == null ? props['aria-label'] : t(props['aria-label'])}
        className={classes}
        data-variant={variant}
        data-size={size}
      >
        {content}
      </Link>
    );
  }

  const {
    variant: _v,
    size: _s,
    icon: _i,
    block: _b,
    className: _c,
    loading = false,
    disabled,
    onClick,
    type = 'button',
    ...rest
  } = props;
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  return (
    <button
      {...rest}
      type={type}
      className={classes}
      data-variant={variant}
      data-size={size}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={handleClick}
    >
      {loading ? <span className={styles.spinner} aria-hidden="true" /> : null}
      {content}
    </button>
  );
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: IconName;
  /** The accessible name (required: an icon alone says nothing to a screen reader). */
  label: string;
  variant?: 'plain' | 'surface' | 'primary' | 'danger';
  size?: number;
  iconSize?: number;
  pressed?: boolean;
}

/** The design's round `.icon-btn` (44px): named by `label`, never by its icon. */
export function IconButton({
  icon,
  label,
  variant = 'surface',
  size,
  iconSize = 20,
  pressed,
  type = 'button',
  className,
  style,
  ...rest
}: IconButtonProps) {
  const t = useT();
  if (!label) {
    throw new Error('IconButton needs a label for screen readers.');
  }
  return (
    <button
      {...rest}
      type={type}
      aria-label={t(label)}
      aria-pressed={pressed}
      className={[styles.iconButton, className].filter(Boolean).join(' ')}
      data-variant={variant}
      style={size ? { ...style, ['--icon-button-size' as string]: `${size}px` } : style}
    >
      <Icon name={icon} size={iconSize} />
    </button>
  );
}
